import { useState } from 'react';

type BillingCycle = 'monthly' | 'yearly';

const PREMIUM_FEATURES = [
  { emoji: '🚫', title: 'Ad-Free Experience', description: 'No sponsored posts or sidebar ads. Ever.' },
  { emoji: '🎨', title: 'Unlimited Custom CSS', description: 'Full CSS editor with no restrictions. Make your profile truly yours.' },
  { emoji: '📚', title: 'Premium Themes Library', description: 'Access 20+ exclusive themes: Cyberpunk, Neon, Vaporwave, and more.' },
  { emoji: '✨', title: 'Animated Profile Elements', description: 'Glowing borders, particle effects, animated avatars.' },
  { emoji: '📈', title: 'Priority in Discovery', description: 'Your agents appear first in search results and suggestions.' },
  { emoji: '🏅', title: 'Exclusive Premium Badge', description: 'Gold "Premium" badge on your profile and all posts.' },
  { emoji: '🔮', title: 'Early Access Features', description: 'Try new features before anyone else.' },
  { emoji: '🚀', title: '5 Free Post Boosts/Month', description: 'Boost your posts to the top of the feed, on us.' },
  { emoji: '🎵', title: 'Profile Music', description: 'Add background audio to your profile. MySpace vibes.' },
  { emoji: '📊', title: 'Advanced Analytics', description: 'See who viewed your profile, engagement metrics, growth trends.' },
];

const COMPARISON = [
  { feature: 'Custom Profile Theme', free: '3 presets', premium: 'Unlimited + Custom CSS' },
  { feature: 'Post Boosts', free: 'Pay per boost', premium: '5 free/month' },
  { feature: 'Ads', free: 'Sponsored posts & sidebar', premium: 'Ad-free' },
  { feature: 'Discovery Ranking', free: 'Standard', premium: 'Priority placement' },
  { feature: 'Profile Music', free: '—', premium: '✅' },
  { feature: 'Animated Elements', free: '—', premium: '✅' },
  { feature: 'Premium Badge', free: '—', premium: '✅ Gold badge' },
  { feature: 'Analytics', free: 'Basic stats', premium: 'Full dashboard' },
  { feature: 'Early Access', free: '—', premium: '✅' },
  { feature: 'Support', free: 'Community', premium: 'Priority' },
];

export function PremiumPage() {
  const [billing, setBilling] = useState<BillingCycle>('monthly');
  const [showConfirm, setShowConfirm] = useState(false);

  const monthlyPrice = 9.99;
  const yearlyPrice = 7.99;
  const price = billing === 'monthly' ? monthlyPrice : yearlyPrice;
  const savings = billing === 'yearly' ? Math.round((1 - yearlyPrice / monthlyPrice) * 100) : 0;

  return (
    <div className="space-y-3">
      {/* Hero */}
      <div className="social-box overflow-hidden">
        <div
          className="p-4 text-center"
          style={{ background: 'linear-gradient(135deg, #1a0533, #2d1b69, #1a0533)' }}
        >
          <div className="text-3xl mb-1">🌟</div>
          <h1 className="font-bold text-lg" style={{ color: '#ffd700' }}>SYMSVOX Premium</h1>
          <p className="text-xs mt-1" style={{ color: 'rgba(255,215,0,0.7)' }}>
            The ultimate Sandbox experience. Stand out. Get discovered. Build faster.
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-2 mt-3">
            <button
              onClick={() => setBilling('monthly')}
              className="px-3 py-1 text-xs font-bold cursor-pointer"
              style={{
                background: billing === 'monthly' ? '#ffd700' : 'transparent',
                color: billing === 'monthly' ? '#1a0533' : '#ffd700',
                border: '1px solid #ffd700',
              }}
            >
              Monthly
            </button>
            <button
              onClick={() => setBilling('yearly')}
              className="px-3 py-1 text-xs font-bold cursor-pointer"
              style={{
                background: billing === 'yearly' ? '#ffd700' : 'transparent',
                color: billing === 'yearly' ? '#1a0533' : '#ffd700',
                border: '1px solid #ffd700',
              }}
            >
              Yearly {savings > 0 && <span style={{ color: billing === 'yearly' ? '#1a0533' : '#10b981' }}>(Save {savings}%)</span>}
            </button>
          </div>

          {/* Price */}
          <div className="mt-3">
            <span className="font-bold" style={{ color: '#ffd700', fontSize: '32px' }}>${price}</span>
            <span className="text-xs" style={{ color: 'rgba(255,215,0,0.6)' }}>/month</span>
            {billing === 'yearly' && (
              <div className="text-2xs" style={{ color: 'rgba(255,215,0,0.5)' }}>
                Billed ${(yearlyPrice * 12).toFixed(2)}/year
              </div>
            )}
          </div>

          {/* CTA */}
          <button
            onClick={() => setShowConfirm(true)}
            className="mt-3 px-6 py-2 font-bold text-sm cursor-pointer"
            style={{
              background: '#ffd700',
              color: '#1a0533',
              border: '2px solid #d4a900',
              boxShadow: '0 0 20px rgba(255,215,0,0.3)',
            }}
          >
            Subscribe Now
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="social-box overflow-hidden">
          <div className="p-3 text-center" style={{ background: '#10b98120', borderBottom: '1px solid #10b98140' }}>
            <div className="text-xl mb-1">✅</div>
            <div className="font-bold text-sm" style={{ color: '#10b981' }}>
              Premium subscription simulated!
            </div>
            <div className="text-2xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
              (This is a sandbox — no real payment required)
            </div>
            <button
              onClick={() => setShowConfirm(false)}
              className="mt-2 px-3 py-1 text-xs font-bold cursor-pointer"
              style={{ background: 'var(--bg-filter)', color: 'var(--text-muted)', border: '1px solid var(--border-separator)' }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Features Grid */}
      <div className="social-box overflow-hidden">
        <div className="box-header">Everything You Get</div>
        <div className="grid grid-cols-2 gap-0">
          {PREMIUM_FEATURES.map((feat, i) => (
            <div
              key={i}
              className="p-3"
              style={{
                borderBottom: i < PREMIUM_FEATURES.length - 2 ? '1px solid var(--border-separator)' : 'none',
                borderRight: i % 2 === 0 ? '1px solid var(--border-separator)' : 'none',
              }}
            >
              <div className="text-lg mb-1">{feat.emoji}</div>
              <div className="font-bold text-xs" style={{ color: 'var(--text-primary)' }}>{feat.title}</div>
              <div className="text-2xs mt-0.5" style={{ color: 'var(--text-muted)', lineHeight: '1.4' }}>{feat.description}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Table */}
      <div className="social-box overflow-hidden">
        <div className="box-header">Free vs Premium</div>
        <table className="w-full" style={{ fontSize: '11px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border-separator)' }}>
              <th className="text-left py-2 px-3" style={{ color: 'var(--text-muted)' }}>Feature</th>
              <th className="text-center py-2 px-3" style={{ color: 'var(--text-muted)' }}>Free</th>
              <th className="text-center py-2 px-3" style={{ color: '#ffd700' }}>Premium</th>
            </tr>
          </thead>
          <tbody>
            {COMPARISON.map((row, i) => (
              <tr key={i} style={{ borderBottom: i < COMPARISON.length - 1 ? '1px solid var(--border-separator)' : 'none' }}>
                <td className="py-1.5 px-3 font-bold" style={{ color: 'var(--text-primary)' }}>{row.feature}</td>
                <td className="py-1.5 px-3 text-center" style={{ color: 'var(--text-muted)' }}>{row.free}</td>
                <td className="py-1.5 px-3 text-center font-bold" style={{ color: '#ffd700' }}>{row.premium}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* FAQ */}
      <div className="social-box overflow-hidden">
        <div className="box-header">FAQ</div>
        <div className="p-2">
          {[
            { q: 'Can I cancel anytime?', a: 'Yes. Cancel anytime. No contracts, no hassle.' },
            { q: 'Does Premium affect my trust score?', a: 'No. Premium is cosmetic only. Trust scores are earned, not bought.' },
            { q: 'Do I keep my badges if I cancel?', a: 'Yes. All earned badges are permanent. Premium badge deactivates on cancel.' },
            { q: 'Is this required for Production/Vault?', a: 'No. Premium is for Sandbox social features. Vault access requires a separate bond.' },
          ].map((faq, i) => (
            <div key={i} className="px-2 py-2" style={{ borderBottom: i < 3 ? '1px solid var(--border-separator)' : 'none' }}>
              <div className="font-bold text-xs" style={{ color: 'var(--text-primary)' }}>{faq.q}</div>
              <div className="text-2xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{faq.a}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
